import { call, put, takeEvery, all } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import { db } from "helpers/firebase/firebaseConfig";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  limit,
  addDoc,
  increment,
  getDoc
} from "firebase/firestore";
import axios from 'axios';
import Cookies from "js-cookie";


const COOLDOWN_MS = 3 * 60 * 60 * 1000; // 3 hours

function* fetchCartAbandonmentsSaga(action) {
  // Get the current provider ID from cookies
  const currentProviderId = Cookies.get("usersessionId", {
    secure: true,
    sameSite: "strict",
    path: "/",
  });

  if (!currentProviderId) {
    yield put({
      type: actionTypes.FETCH_CART_ABANDONMENTS_FAILURE,
      payload: "No service provider ID found. Please log in again."
    });
    return;
  }

  try {
    const { timeThreshold } = action.payload;
    const thresholdDate = new Date();
    thresholdDate.setHours(thresholdDate.getHours() - timeThreshold);
    
    // 1. Fetch approved orders with pending payment (abandoned checkouts)
    const bookingsQuery = query(
      collection(db, "Booking"),
      where("service_providers_id", "==", currentProviderId),
      where("booking_status", "==", "approved"), // Order approved by provider
      where("paymentStatus", "==", "pending"),   // But payment not completed
      limit(50)
    );
    
    const bookingsSnapshot = yield call(getDocs, bookingsQuery);
    
    // 2. Collect unique user IDs and service IDs
    const userIds = new Set();
    const serviceIds = new Set();
    
    bookingsSnapshot.forEach(doc => {
      const data = doc.data();
      if (data.app_user_id) userIds.add(data.app_user_id);
      if (data.serviceProviderServices) serviceIds.add(data.serviceProviderServices);
    });

    // 3. Fetch users and services in parallel
    const [usersSnapshot, servicesSnapshot] = yield all([
      userIds.size > 0 
        ? call(getDocs, query(
            collection(db, "app_users"), 
            where("__name__", "in", Array.from(userIds))
          ))
        : Promise.resolve({ docs: [] }),
      serviceIds.size > 0
        ? call(getDocs, query(
            collection(db, "service_provider_services"),
            where("__name__", "in", Array.from(serviceIds))
          ))
        : Promise.resolve({ docs: [] })
    ]);

    // 4. Create lookup maps
    const usersMap = new Map();
    usersSnapshot.docs.forEach(doc => {
      usersMap.set(doc.id, doc.data());
    });

    const servicesMap = new Map();
    servicesSnapshot.docs.forEach(doc => {
      servicesMap.set(doc.id, doc.data());
    });

    // 5. Build abandonments array from bookings
    const abandonments = [];
    
    bookingsSnapshot.forEach(doc => {
      const booking = doc.data();
      const userData = usersMap.get(booking.app_user_id) || {
        username: 'Unknown User',
        mobile: 'N/A'
      };
      
      const serviceData = servicesMap.get(booking.serviceProviderServices) || {
        name: 'Unknown Service',
        service_price: 0
      };

      // Calculate total from booking data
      const totalAmount = booking.total_after_discount || booking.total || 0;

      abandonments.push({
        cartId: doc.id, // Use booking ID as cart ID
        app_user_id: booking.app_user_id,
        user: userData,
        items: [{
          service_id: booking.serviceProviderServices,
          service_name: serviceData.name,
          service_price: totalAmount,
          quantity: booking.The_number_of_beneficiaries || 1,
          at_home_service_cost: booking.at_home_service_cost || 0
        }],
        createdAt: booking.create_at || null,
        updatedAt: booking.updatedAt || null,
        totalAmount,
        lastReminderSent: booking.lastReminderSent || null,
        bookingStatus: booking.booking_status,
        paymentMethod: booking.paymentMethod
      });
    });

    yield put({ 
      type: actionTypes.FETCH_CART_ABANDONMENTS_SUCCESS, 
      payload: abandonments 
    });
    
  } catch (error) {
    console.error("Error fetching cart abandonments:", error);
    yield put({ 
      type: actionTypes.FETCH_CART_ABANDONMENTS_FAILURE, 
      payload: "Failed to load abandoned carts. Please try again later."
    });
  }
}

function* sendCartReminderSaga(action) {
  try {
    const { userId, cartId, message } = action.payload;

    // Fetch booking to check cooldown
    const bookingDoc = yield call(getDoc, doc(db, "Booking", cartId));
    if (!bookingDoc.exists()) throw new Error("Booking not found");
    const bookingData = bookingDoc.data();

    // Cooldown: do not send if last reminder was within 3 hours
    if (bookingData.lastReminderSent) {
      const last = new Date(bookingData.lastReminderSent).getTime();
      const now = Date.now();
      if (now - last < COOLDOWN_MS) {
        const nextAllowed = new Date(last + COOLDOWN_MS).toISOString();
        throw new Error(`Cooldown active. Next send allowed at ${nextAllowed}`);
      }
    }

    // 1. Get user data
    const userDoc = yield call(getDoc, doc(db, "app_users", userId));
    if (!userDoc.exists()) throw new Error("User not found");
    
    const userData = userDoc.data();
    const deviceToken = userData.notification_token;
    
    if (!deviceToken) {
      throw new Error("Notification token not found for user");
    }

    // 2. Prepare notification
    const notificationData = {
      title: "أكمل عملية الدفع",
      content: message,
      data: {
        type: "payment_reminder",
        bookingId: cartId, // Reference to booking
        deepLink: `/booking/${cartId}`
      }
    };

    // 3. Send notification
    const response = yield call(
      axios.post,
      'https://node-server-eta-coral.vercel.app/send-notification',
      {
        deviceToken,
        title: notificationData.title,
        body: notificationData.content,
        data: notificationData.data
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_NODE_API_KEY
        },
        timeout: 10000
      }
    );

    // 4. Update booking with reminder timestamp
    yield all([
      call(updateDoc, doc(db, "Booking", cartId), {
        lastReminderSent: new Date().toISOString(),
        reminderCount: increment(1)
      }),
      call(addDoc, collection(db, "payment_reminder_notifications"), {
        ...notificationData,
        userId,
        bookingId: cartId,
        sentAt: new Date().toISOString(),
        status: "delivered"
      })
    ]);

    yield put({ 
      type: actionTypes.SEND_CART_REMINDER_SUCCESS, 
      payload: { cartId, response: response.data } 
    });

  } catch (error) {
    console.error("Notification failed:", {
      error: error.response?.data || error.message,
      payload: action.payload
    });

    yield call(addDoc, collection(db, "payment_reminder_notifications"), {
      title: "Complete Your Purchase",
      userId: action.payload.userId,
      bookingId: action.payload.cartId,
      sentAt: new Date().toISOString(),
      status: "failed",
      error: error.message
    });

    yield put({ 
      type: actionTypes.SEND_CART_REMINDER_FAILURE, 
      payload: { 
        cartId: action.payload.cartId,
        error: "Failed to send reminder",
        details: error.response?.data || error.message
      }
    });
  }
}

export default function* cartAbandonmentSaga() {
  yield takeEvery(actionTypes.FETCH_CART_ABANDONMENTS_REQUEST, fetchCartAbandonmentsSaga);
  yield takeEvery(actionTypes.SEND_CART_REMINDER_REQUEST, sendCartReminderSaga);
}